import {
  ARCADE_CHAPTERS,
  COLOR_CHAPTERS,
  COSMETIC_BY_ID,
  FILL_CHAPTERS,
  loadArcadeChapter,
  loadColorChapter,
  loadFillChapter,
  loadSortingChapter,
  loadTileChapter,
  LOGIC_CHAPTERS,
  loadLogicChapter,
  SORTING_CHAPTERS,
  TILE_CHAPTERS,
} from "@nano/content";
import type { HouseId } from "@nano/game-config";
import { useEffect, useMemo, useState } from "react";
import { closeHousePopup, openOverlay, startLevel } from "../../app/actions.ts";
import { bridge } from "../../app/bridge.ts";
import { shallowEqual, useApp } from "../../app/store.ts";
import { chapterFocus, chapterName, houseDescription, houseName, itemName } from "../../i18n/content.ts";
import { formatDuration, vi } from "../../i18n/vi.ts";
import { claimableCount } from "../../services/achievements.ts";
import { bestStars, bestTime, highestCompleted, isUnlocked, nextLevel, pendingGold } from "../../services/save/progress.ts";
import { GiftChestButton } from "../ads/AdsUi.tsx";
import { Art, Button, Coins, Sheet, Stars } from "../components/common.tsx";
import { UpdateBanner } from "./Updates.tsx";

export function CityHud() {
  const { save, outbox, sync, cityReady } = useApp(
    (s) => ({ save: s.save, outbox: s.outbox, sync: s.ui.sync, cityReady: s.ui.cityReady }),
    shallowEqual,
  );
  if (!save) return null;
  const gold = save.server?.wallet.gold ?? 0;
  const pending = pendingGold(outbox);
  const showHint = cityReady && !save.profile.cityHintDone && highestCompleted(save, "sorting") === 0;
  const claimable = claimableCount(save);
  return (
    <div className="hud" data-testid="city-hud">
      <header className="hud__top">
        <div className="hud__player">
          <span className="hud__name">{save.profile.displayName ?? save.server?.publicTag ?? vi.hud.guest}</span>
          <span className={`hud__sync hud__sync--${sync}`} title={vi.settings.syncState[sync]} />
        </div>
        <div className="hud__wallet">
          <Coins amount={gold} pending={pending} />
          <WishlistGoal itemId={save.wishlist} gold={gold} />
        </div>
      </header>
      {showHint ? null : <GiftChestButton />}
      <UpdateBanner />
      {showHint ? (
        <div className="hud__hint" data-testid="city-hint">
          {vi.hud.firstHint}
        </div>
      ) : null}
      <nav ref={reportBottomInset} className="hud__bottom hud__bottom--five">
        <HudButton icon="icon.shop" label={vi.hud.shop} onClick={() => openOverlay("shop")} testId="open-shop" />
        <HudButton icon="icon.wardrobe" label={vi.hud.wardrobe} onClick={() => openOverlay("wardrobe")} testId="open-wardrobe" />
        <HudButton
          icon="icon.medal"
          label={vi.hud.achievements}
          onClick={() => openOverlay("achievements")}
          testId="open-achievements"
          badge={claimable}
        />
        <HudButton icon="icon.trophy" label={vi.hud.leaderboard} onClick={() => openOverlay("leaderboard")} testId="open-leaderboard" />
        <HudButton icon="icon.settings" label={vi.hud.settings} onClick={() => openOverlay("settings")} testId="open-settings" />
      </nav>
    </div>
  );
}

/** Tells the city how much of the screen the nav covers, so the bottom houses can scroll clear of it. */
function reportBottomInset(nav: HTMLElement | null): (() => void) | undefined {
  const hud = nav?.closest(".hud");
  if (!nav || !hud) return undefined;
  const measure = () => bridge.emit("city:bottom-inset", Math.ceil(hud.getBoundingClientRect().bottom - nav.getBoundingClientRect().top));
  measure();
  const observer = new ResizeObserver(measure);
  observer.observe(nav);
  window.addEventListener("resize", measure);
  return () => {
    observer.disconnect();
    window.removeEventListener("resize", measure);
  };
}

function HudButton({ icon, label, onClick, testId, badge }: { icon: string; label: string; onClick: () => void; testId: string; badge?: number }) {
  return (
    <button type="button" className="hud-btn" onClick={onClick} data-testid={testId}>
      <span className="hud-btn__iconwrap">
        <Art id={icon} className="hud-btn__icon" />
        {badge ? <span className="hud-btn__badge">{badge}</span> : null}
      </span>
      <span className="hud-btn__label">{label}</span>
    </button>
  );
}

/** The item the player is saving for, with how far the gold has come. */
function WishlistGoal({ itemId, gold }: { itemId: string | null; gold: number }) {
  const item = itemId ? COSMETIC_BY_ID.get(itemId) : undefined;
  if (!item) return null;
  const ready = gold >= item.priceGold;
  const pct = Math.min(100, Math.round((gold / Math.max(1, item.priceGold)) * 100));
  return (
    <button type="button" className={"goal" + (ready ? " goal--ready" : "")} onClick={() => openOverlay("shop")} data-testid="wishlist-goal">
      <Art id={item.assetIds.front} className="goal__art" alt={itemName(item)} />
      <span className="goal__body">
        <span className="goal__label">{ready ? vi.shop.goalReady : vi.shop.goalLeft(item.priceGold - gold)}</span>
        <span className="goal__bar">
          <span style={{ width: pct + "%" }} />
        </span>
      </span>
    </button>
  );
}

/** Houses with levels to pick from. */
type LobbyHouse = "sorting" | "colors" | "tiles" | "logic" | "arcade" | "fill";
const CHAPTERS = { sorting: SORTING_CHAPTERS, colors: COLOR_CHAPTERS, tiles: TILE_CHAPTERS, logic: LOGIC_CHAPTERS, arcade: ARCADE_CHAPTERS, fill: FILL_CHAPTERS } as const;

/** Difficulty (1-5) of each level in a chapter, read from its lazily loaded pack. */
function useChapterDifficulty(houseId: LobbyHouse, chapter: number): ReadonlyMap<number, number> {
  const [map, setMap] = useState<ReadonlyMap<number, number>>(new Map());
  useEffect(() => {
    let alive = true;
    const load =
      houseId === "fill"
        ? loadFillChapter(chapter)
        : houseId === "arcade"
        ? loadArcadeChapter(chapter)
        : houseId === "logic"
        ? loadLogicChapter(chapter)
        : houseId === "tiles"
          ? loadTileChapter(chapter)
          : houseId === "colors"
            ? loadColorChapter(chapter)
            : loadSortingChapter(chapter);
    load.then((pack) => alive && setMap(new Map(pack.levels.map((level) => [level.id, level.difficulty])))).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [houseId, chapter]);
  return map;
}

export function HouseLobby({ houseId: id }: { houseId: HouseId }) {
  const houseId: LobbyHouse = id === "colors" || id === "tiles" || id === "logic" || id === "arcade" || id === "fill" ? id : "sorting";
  const chapters = CHAPTERS[houseId];
  const { save, config, loading } = useApp((s) => ({ save: s.save, config: s.ui.config, loading: s.ui.levelLoading }), shallowEqual);
  const next = save ? nextLevel(save, houseId) : 1;
  const [chapter, setChapter] = useState(Math.ceil(next / 10));
  const house = config.houses.find((h) => h.id === houseId);
  const difficulty = useChapterDifficulty(houseId, chapter);
  if (!save || !house) return null;
  const done = highestCompleted(save, houseId);
  const current = chapters[chapter - 1];
  const chapterStars = useMemo(
    () => (current ? Array.from({ length: 10 }, (_, i) => bestStars(save, houseId, current.firstLevel + i) ?? 0).reduce<number>((a, b) => a + b, 0) : 0),
    [save, houseId, current],
  );
  return (
    <Sheet title={houseName(house)} onClose={() => closeHousePopup()} className="lobby" testId="house-lobby">
      <div className="lobby__hero">
        <Art id={house.buildingAssetId} className="lobby__art" alt={houseName(house)} />
        <p className="lobby__desc">{houseDescription(house)}</p>
      </div>
      <div className="lobby__progress">
        <span>{vi.lobby.current(Math.min(done + 1, 100))}</span>
        <div className="bar">
          <span style={{ width: `${done}%` }} />
        </div>
      </div>
      <Button variant="primary" onClick={() => void startLevel(houseId, next)} disabled={loading} testId="lobby-play" className="lobby__play">
        {done >= 100 ? vi.lobby.allDone : done === 0 ? vi.lobby.playLevel(1) : vi.lobby.playNext(next)}
      </Button>
      <div className="chapters" role="tablist">
        {chapters.map((c) => (
          <button
            key={c.chapter}
            type="button"
            role="tab"
            aria-selected={c.chapter === chapter}
            className={`chapters__tab ${c.chapter === chapter ? "chapters__tab--active" : ""} ${
              isUnlocked(save, houseId, c.firstLevel) ? "" : "chapters__tab--locked"
            }`}
            aria-label={vi.lobby.chapterName(c.chapter, chapterName(houseId, c))}
            onClick={() => setChapter(c.chapter)}
          >
            {c.chapter}
          </button>
        ))}
      </div>
      {current ? (
        <div className="chapter">
          <h3 className="chapter__title">
            {vi.lobby.chapterName(current.chapter, chapterName(houseId, current))}
          </h3>
          <p className="chapter__focus">
            {chapterFocus(houseId, current)}
            <span className="chapter__stars">
              <Art id="reward.star" className="chapter__star" /> {vi.lobby.chapterStars(chapterStars)}
            </span>
          </p>
          {/* Only the selected chapter's 10 levels are in the DOM. */}
          <div className="levels">
            {Array.from({ length: 10 }, (_, i) => current.firstLevel + i).map((levelId) => {
              const unlocked = isUnlocked(save, houseId, levelId);
              const stars = bestStars(save, houseId, levelId);
              const time = bestTime(save, houseId, levelId);
              const diff = difficulty.get(levelId);
              return (
                <button
                  key={levelId}
                  type="button"
                  className={`level ${unlocked ? "" : "level--locked"} ${stars ? "level--done" : ""} ${levelId % 10 === 0 ? "level--finale" : ""}`}
                  disabled={!unlocked || loading}
                  onClick={() => void startLevel(houseId, levelId)}
                  data-testid={`level-${levelId}`}
                >
                  <span className="level__number">{levelId}</span>
                  {stars ? <Stars count={stars} size={12} /> : unlocked ? null : <Art id="reward.lock" className="level__lock" />}
                  {time !== null ? <span className="level__time">{formatDuration(time)}</span> : null}
                  {diff && unlocked ? (
                    <span className="level__diff" aria-label={vi.puzzle.difficulty[diff]}>
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className={i < diff ? "on" : ""} />
                      ))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}

export function HouseInfo({ houseId }: { houseId: HouseId }) {
  const house = useApp((s) => s.ui.config.houses.find((h) => h.id === houseId));
  if (!house) return null;
  const maintenance = house.status === "maintenance";
  return (
    <Sheet title={houseName(house)} onClose={() => closeHousePopup()} testId="house-info">
      <div className="info">
        <Art id={`${house.buildingAssetId}.construction`} className="info__art" alt={houseName(house)} />
        <div className="info__badge">
          <Art id="reward.hardhat" className="info__hat" />
          <strong>{maintenance ? vi.construction.maintenance : vi.construction.title}</strong>
        </div>
        <p>{maintenance ? vi.construction.maintenanceBody : vi.construction.body}</p>
        <p className="note">{houseDescription(house)}</p>
      </div>
      <Button variant="secondary" onClick={() => closeHousePopup()}>
        {vi.ok}
      </Button>
    </Sheet>
  );
}

